import { useEffect, useState } from 'react';
import { Clock, AlertCircle, Loader2, ArrowRight } from 'lucide-react';

import Card, { CardHeader } from '../ui/Card';
import Badge from '../ui/Badge';
import { listRecentCloses } from '../../api/mec';
import { formatMoney, formatRelative, formatDate } from '../../utils/formatters';

// RecentClosesPanel — surfaces up to 10 most-recent close runs across the
// user's accessible shops so the bookkeeper can re-open a prior close without
// hunting through the period picker.
//
// Each row click → onRowClick({ shop_id, period_start, period_end }) so the
// parent (Dashboard) can deep-link to /mec/location/:shop_id?start=&end=.

function statusTone(status) {
  switch (status) {
    case 'finalized': return 'recovery';
    case 'reopened':  return 'action';
    case 'draft':     return 'cream';
    default:          return 'neutral';
  }
}

export default function RecentClosesPanel({ onRowClick, limit = 10 }) {
  const [rows, setRows] = useState(null); // null = loading, [] = empty
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setError(null);
    setRows(null);
    listRecentCloses(limit)
      .then((data) => {
        if (!alive) return;
        const list = Array.isArray(data) ? data : (data?.closes || []);
        setRows(list);
      })
      .catch((err) => {
        if (!alive) return;
        setError(err?.message || 'Failed to load recent closes');
        setRows([]);
      });
    return () => { alive = false; };
  }, [limit]);

  return (
    <Card>
      <CardHeader
        title="Recent closes"
        description="Jump back into a previously-run close run. Up to 10 most-recent across your shops."
      />

      {error && (
        <div className="flex items-start gap-2 text-[12px] text-[var(--color-danger-700)] mb-3">
          <AlertCircle size={13} className="shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {rows === null ? (
        <div className="flex items-center gap-2 justify-center py-8 text-[12px] text-[var(--color-warm-500)]">
          <Loader2 size={13} className="animate-spin" />
          Loading recent closes…
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center py-8">
          <Clock size={22} className="mx-auto mb-2 text-[var(--color-warm-400)]" />
          <div className="text-[13px] font-semibold text-[var(--color-warm-700)]">
            No close runs yet
          </div>
          <div className="text-[11px] text-[var(--color-warm-500)] mt-1">
            Run your first close to see it listed here.
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto -mx-1">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-[var(--color-warm-100)]">
                <Th>Shop</Th>
                <Th>Period</Th>
                <Th>Status</Th>
                <Th align="right">Variance</Th>
                <Th align="right">Created</Th>
                <Th width={32} />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const variance = r.variance_cents || 0;
                const varianceColor =
                  variance > 0
                    ? 'text-[var(--color-danger-700)]'
                    : variance < 0
                      ? 'text-[var(--color-recovery-700)]'
                      : 'text-[var(--color-warm-700)]';
                const ts = r.finalized_at || r.created_at;
                const handleClick = () =>
                  onRowClick?.({
                    shop_id: r.shop_id,
                    period_start: r.period_start,
                    period_end: r.period_end,
                  });
                return (
                  <tr
                    key={`${r.close_run_id}`}
                    onClick={handleClick}
                    className="border-b border-[var(--color-warm-100)] last:border-b-0 hover:bg-[var(--color-cream-100)] transition-colors cursor-pointer"
                  >
                    <td className="px-3 py-2.5 align-middle">
                      <div className="font-semibold text-[var(--color-warm-900)]">
                        {r.shop_name || `Shop ${r.shop_id}`}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 align-middle text-[12px] text-[var(--color-warm-600)] tabular-nums">
                      {r.period_start && r.period_end ? (
                        <>
                          {formatDate(r.period_start)}
                          <span className="mx-1 text-[var(--color-warm-400)]">→</span>
                          {formatDate(r.period_end)}
                        </>
                      ) : '—'}
                    </td>
                    <td className="px-3 py-2.5 align-middle">
                      <Badge tone={statusTone(r.status)} size="xs">
                        {r.status || 'draft'}
                      </Badge>
                    </td>
                    <td className={`px-3 py-2.5 align-middle text-right tabular-nums font-semibold ${varianceColor}`}>
                      {(variance >= 0 ? '+' : '') + formatMoney(variance / 100)}
                    </td>
                    <td className="px-3 py-2.5 align-middle text-right text-[11px] text-[var(--color-warm-500)]">
                      {ts ? formatRelative(ts) : '—'}
                    </td>
                    <td className="px-2 py-2.5 align-middle text-right">
                      <ArrowRight size={13} className="text-[var(--color-warm-400)] inline" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function Th({ children, align = 'left', width }) {
  return (
    <th
      style={width ? { width: `${width}px` } : undefined}
      className={`
        px-3 py-2 text-[10px] font-bold uppercase tracking-[1px]
        text-[var(--color-warm-500)]
        ${align === 'right' ? 'text-right' : 'text-left'}
      `}
    >
      {children}
    </th>
  );
}
